import { type IExistContact, useGetExistsContact } from "@api";
import type { IContact } from "@bbj-types/contact";
import type { ContactType } from "@bbj-types/contact/enum";
import type { TContactForm } from "@candidate/components/contactForm/logics";
import { useGlobalContext } from "@root/context";
import { useCallback, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useModalOfferContext } from "../../context";
import type { ContactInputMode } from "./types";

export const getContactOptionLabel = (contact: IContact) => {
  if (contact.firstName && contact.lastName) {
    return `${contact.firstName} ${contact.lastName}`;
  }
  if (contact.firstName) {
    return contact.firstName;
  }
  if (contact.lastName) {
    return contact.lastName;
  }
  if (contact.email) {
    return contact.email;
  }
  if (contact.phone) {
    return contact.phone;
  }
  return "Inconnu";
};

export const useContactInput = (type: ContactType) => {
  const { user } = useGlobalContext();
  const [{ loading }, getExistsContact] = useGetExistsContact();
  const {
    enterprise,
    contact: contextContact,
    setContact: setContextContact,
    isSharedEnterprise,
  } = useModalOfferContext();
  const [expanded, setExpanded] = useState(false);
  const [contact, setContact] = useState<IContact | null>({
    _id: "",
    firstName: "",
    lastName: "",
    job: "",
    email: "",
    phone: "",
    description: "",
    organisation: "",
    isTemp: isSharedEnterprise,
    place: user.organisation.places,
    type,
    createdAt: "",
    updatedAt: "",
  });
  const [mode, setMode] = useState<ContactInputMode>("");
  const [alreadyExistsContact, setAlreadyExistsContact] = useState<IExistContact>({
    contact: null,
    enterpriseId: null,
  });

  const changeMode = useCallback(
    (mode: string) => {
      switch (mode) {
        case "create":
          setMode(mode);
          if (contextContact) {
            setContact(contextContact);
            setContextContact(null);
          } else {
            setContact({
              _id: "",
              firstName: "",
              lastName: "",
              job: "",
              email: "",
              phone: "",
              description: "",
              organisation: "",
              isTemp: isSharedEnterprise,
              place: user.organisation.places,
              type,
              createdAt: "",
              updatedAt: "",
            });
          }
          break;
        case "select":
          setMode(mode);
          setContact(contextContact);
          break;
        case "":
          setMode(mode);
          break;
      }
    },
    [contextContact]
  );

  const handleReset = useCallback(() => {
    setExpanded(false);
    setContact(null);
    setAlreadyExistsContact({ contact: null, enterpriseId: null });
  }, []);

  const handleSubmit = useCallback(
    (optContact?: IContact) => {
      const nContact = optContact || contact;
      if (nContact?._id) {
        setContextContact(nContact);
        handleReset();
      }
      return false;
    },
    [contact, setContextContact, handleReset]
  );

  const onSubmitCreate = useCallback(
    async (data: TContactForm, hookForm: UseFormReturn<TContactForm>) => {
      const nContact: IContact = {
        ...data,
        _id: "",
        organisation: "",
        isTemp: !isSharedEnterprise,
        createdAt: "",
        updatedAt: "",
        type,
      };
      try {
        const response = await getExistsContact(nContact.email, nContact.phone);
        if (response.status === 204) {
          setContextContact(nContact);
          handleReset();
          hookForm.reset();
        } else if (response.data) {
          setAlreadyExistsContact(response.data);
        }
      } catch (err) {
        console.error(err);
      }
    },
    [getExistsContact, setContextContact, handleReset, isSharedEnterprise, type]
  );

  const handleCancel = useCallback(() => {
    setContextContact(null);
    changeMode("");
    handleReset();
    return true;
  }, [handleReset, setContextContact]);

  const contactsNumber = enterprise?.contacts?.length || 0;

  return {
    expanded,
    setExpanded,
    loading,
    getExistsContact,
    enterprise,
    contactsNumber,
    contact,
    contextContact,
    alreadyExistsContact,
    setContextContact,
    mode,
    handleSubmit,
    onSubmitCreate,
    handleCancel,
    setContact,
    changeMode,
    isSharedEnterprise,
  };
};
